import { computed, effect, inject, Injectable } from '@angular/core';
import { ComponentStoreV2Factory } from '../../../shared/store/common/component-store-v2.factory';
import { router, translate } from '../../../shared/shared.global-service';
import { hasSubMenu, LayoutService } from '../../services/layout.service';
import { MenuItem } from '@openng/optimus-ui/api';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { ServiceTypeEnum, serviceTypeNameAllowed } from '@libs/types/src/services/enum/service-type.enum';
import { Operations, ServiceGridItemObject } from '@it2go/types';
import {
    SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY,
    SERVICE_SETTINGS_CALENDAR_TYPE_KEY,
    SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY,
    SERVICE_SETTINGS_DIET_TYPE_KEY,
    SERVICE_SETTINGS_PROVIDED_OPERATIONS_REPORTS_KEY,
    SOCIAL_SERVICE_SETTINGS_FINANCIAL_DEPOSIT_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { IsActiveMatchOptions } from '@angular/router';
import { isOffline } from '../../../../utils/connection.service';
import {
    getStatisticsGroupTypes,
    StatisticsTypeGroupEnum,
} from '@libs/types/src/services/enum/statistics/statistics-type.enum';
import {
    getSocialStatisticGroupByType,
    getSocialStatisticsGroupTypes,
    SocialStatisticsTypeEnum,
    SocialStatisticsTypeGroupEnum,
} from '@libs/types/src/services/enum/statistics/social-statistics-type.enum';
import { AppMenuItem } from './menu.type';
import { featureOn } from '../../../../feature-flag';
import {
    servicesWithAllowedProvidedOperationsReports,
} from '../../../../components/social-service/provided-operations-reports/provided-operations-reports.utils';
import {
    SOCIAL_SERVICE_CONTRACTS_DEFAULT,
    SOCIAL_SERVICE_CONTRACTS_KEY,
    SocialServiceSettingsContractsType,
} from '@libs/types/src/services/types/social-service-settings-contracts.type';
import { getServiceSettings } from '../../../../utils/service.utils';
import { ActionEnum } from '@libs/types/src/authorization/enum/action.enum';
import { healthcareCalendarOperation } from '../../../../utils/healthcare-calendar-acl.utils';
import { healthcareCalendarSettingsKey } from '../../../../utils/healthcare-calendar-settings.utils';

@Injectable({
    providedIn: 'root',
})
export class MenuStore extends ComponentStoreV2Factory {

    protected readonly layoutService = inject(LayoutService);

    services = this.globalGlobalStore.organizationStore.services;

    public sidebarOptions = computed(() => {
        const menu = this.layoutService.config();
        const options: MenuItem[] = [];
        const services = this.services();
        const expertiseCodes = services?.map(s => s.expertise?.code || '') || [];
        const allowedHealthCareAndSocial = this.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS)() ?? true;

        const useDiet = this.useCard(
            services,
            ServiceTypeEnum.SOCIAL,
            SERVICE_SETTINGS_DIET_TYPE_KEY,
            (service: ServiceGridItemObject) => serviceTypeNameAllowed.diets.includes(service.serviceType.name),
        );
        const useBasicSocialConsulting = this.useCard(
            services,
            ServiceTypeEnum.SOCIAL,
            SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY,
        );
        const useContinuousRecords = this.useCard(
            services,
            ServiceTypeEnum.SOCIAL,
            SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY,
        );

        const hasSocialService = allowedHealthCareAndSocial && !!services?.find((it) => it.type === ServiceTypeEnum.SOCIAL);
        const hasHealthCareService = allowedHealthCareAndSocial && !!services?.find((it) => it.type === ServiceTypeEnum.HEALTH_CARE);
        const isAllowed = (...operation: Operations[]) => this.globalGlobalStore.isAllowed(...operation);

        if (isAllowed('serviceDashboard.read')() && allowedHealthCareAndSocial) {
            this.prepMenuOption(options, 'dashboard', '/content/dashboard', 'pi pi-fw pi-home');
        }
        if (featureOn.NEW_CALENDAR && isAllowed('myCalendar.read')()) {
            this.prepMenuOption(options, 'calendar', '/content/my-calendar', 'pi pi-fw pi-calendar');
        }

        if (featureOn.DATABOXES && isAllowed('databox.read')()) {
            this.prepMenuOption(options, 'databoxes', '/databoxes', 'pi pi-fw pi-inbox');
        }
        if (isAllowed('dms.read')()) {
            this.prepMenuOption(options, 'dms', '/dms', 'pi pi-pw pi-inbox');
        }
        if (featureOn.EMAIL_CLIENT && isAllowed('emailClient.read')()) {
            this.prepMenuOption(options, 'emailClient', '/email', 'pi pi-pw pi-inbox');
        }
        if (isAllowed('abStats.read')()) {
            this.prepMenuOption(options, 'abStats', '/ab-stats', 'pi pi-pw pi-chart-bar');
        }
        if (isAllowed('editorial.read')()) {
            this.prepMenuOption(options, 'editorial', '/content/editorial-poc', 'pi pi-fw pi-file-edit');
        }
        if (isAllowed('deskaMonitor.read')() && featureOn.DESKA_MONITOR) {
            const deskaMonitor = this.prepMenuOption(
                options,
                'deskaMonitor',
                undefined,
                'pi pi-pw pi-eye',
            );
            this.prepMenuOption(
                deskaMonitor.items,
                'deskaMonitorFindings',
                '/content/deska-monitor',
                'pi pi-fw pi-search',
            );
            this.prepMenuOption(
                deskaMonitor.items,
                'deskaMonitorImport',
                '/content/deska-monitor/import',
                'pi pi-fw pi-upload',
            );
            this.prepMenuOption(
                deskaMonitor.items,
                'deskaMonitorKeywords',
                '/content/deska-monitor/keywords',
                'pi pi-fw pi-tags',
            );
            this.prepMenuOption(
                deskaMonitor.items,
                'deskaMonitorRecipients',
                '/content/deska-monitor/recipients',
                'pi pi-fw pi-envelope',
            );
            this.prepMenuOption(
                deskaMonitor.items,
                'deskaMonitorRuns',
                '/content/deska-monitor/runs',
                'pi pi-fw pi-history',
            );
        }

        const readDonation = isAllowed('donation.read')();
        const collectionStatisticsRead = isAllowed('collectionStatistics.read')();
        if (readDonation || collectionStatisticsRead) {
            const opt = this.prepMenuOption(options, 'crm', undefined, 'pi pi-pw pi-file', undefined);
            if (readDonation) {
                this.prepMenuOption(opt.items, 'warnings', '/crm/warnings', 'pi pi-fw pi-exclamation-triangle');
                this.prepMenuOption(opt.items, 'donors', '/crm/donors', 'pi pi-fw pi-external-link');
                this.prepMenuOption(opt.items, 'donations', '/crm/donations', 'pi pi-fw pi-external-link');
            }
            if (readDonation || collectionStatisticsRead) {
                this.prepMenuOption(opt.items, 'collections', '/crm/collections', 'pi pi-fw pi-external-link');
            }
            if (readDonation) {
                this.prepMenuOption(opt.items, 'summaryEmails', '/crm/summary-emails', 'pi pi-fw pi-external-link');
                this.prepMenuOption(opt.items, 'activities', '/crm/activities', 'pi pi-fw pi-external-link');
                this.prepMenuOption(opt.items, 'settings', '/crm/settings', 'pi pi-fw pi-external-link');
            }
        }

        const createWork = isAllowed('work.create')();
        const readWork = isAllowed('work.read')();
        const operationalWorkRead = isAllowed('operationalWork.read')();
        const operationalWorkCreate = isAllowed('operationalWork.create')();
        const readFolder = isAllowed('folder.read')();
        const readFile = isAllowed('files.read')();
        const readDoctorWork = isAllowed('doctorWork.read')();
        const readSpecialistWork = isAllowed('specialistWork.read')();
        const continuousReportCreate = isAllowed('continuousReport.create')();
        const workCreate = isAllowed('work.create')();
        const continuousReportRead = isAllowed('continuousReport.read')();
        const individualWorkCreate = isAllowed('individualWork.create')();
        const individualWorkRead = isAllowed('individualWork.read')();
        const workSuggestionCreate = isAllowed('workSuggestion.create')();
        const workSuggestionRead = isAllowed('workSuggestion.read')();
        const tangibleDepositCreate = isAllowed('tangibleDeposit.create')();
        const tangibleDepositRead = isAllowed('tangibleDeposit.read')();
        const workMovementRestrictionCreate = isAllowed('workMovementRestriction.create')();
        const workMovementRestrictionRead = isAllowed('workMovementRestriction.read')();
        const workSanctionCreate = isAllowed('workSanction.create')();
        const workSanctionRead = isAllowed('workSanction.read')();
        const workGroupActivityCreate = isAllowed('workGroupActivity.create')();
        const workGroupActivityRead = isAllowed('workGroupActivity.read')();
        const workMassWorkCreate = isAllowed('workMassWork.create')();
        const workMassWorkRead = isAllowed('workMassWork.read')();
        const pensionsIncomeRead = isAllowed('pensionsIncome.read')();
        const contractualSurchargeIncomeRead = isAllowed('contractualSurchargeIncome.read')();
        const financialDepositBulkCreate = isAllowed('financialDepositBulk.create')();
        const deficitStatusRead = isAllowed('deficitStatus.read')();
        const financialMovementsRead = isAllowed('financialMovements.read')();
        const careContributionRefundRead = isAllowed('careContributionRefund.read')();
        const achoAnalysisRead = isAllowed('achoAnalysis.read')();
        const providedOperationsReportRead = isAllowed('providedOperationsReport.read')();

        // Social services
        if (hasSocialService) { // this needs to be changed for both social and health services otherwise they are both shown
            const sublinks: MenuItem[] = [];
            if (readFolder) {
                this.prepMenuOption(sublinks, 'client-folders', '/content/social/folders', 'pi pi-fw pi-folder');
            }
            if (readFile) {
                this.prepMenuOption(sublinks, 'files-manager', '/content/social/files', 'pi pi-fw pi-server');
            }
            if (
                continuousReportCreate
                || workCreate
                || individualWorkCreate
                || workSuggestionCreate
                || tangibleDepositCreate
                || workMovementRestrictionCreate
                || workSanctionCreate
                || workGroupActivityCreate
                || workMassWorkCreate
                || (featureOn.NEW_CALENDAR && operationalWorkCreate)
            ) {
                this.prepMenuOption(sublinks, 'works', '/content/social/works', 'pi pi-fw pi-plus');
            }
            if (
                continuousReportRead
                || workCreate
                || individualWorkRead
                || workSuggestionRead
                || tangibleDepositRead
                || workMovementRestrictionRead
                || workSanctionRead
                || workGroupActivityRead
                || workMassWorkRead
                || operationalWorkRead
            ) {
                const workItems = this.prepMenuOption(sublinks, 'realizedWork', undefined, 'pi pi-pw pi-briefcase', undefined);
                if (individualWorkRead) {
                    this.prepMenuOption(workItems.items, 'workIndividualWork', '/content/social/realized-work/list/individual-work');
                }
                if (workGroupActivityRead) {
                    this.prepMenuOption(workItems.items, 'workGroupActivity', '/content/social/realized-work/list/group-activity');
                    this.prepMenuOption(workItems.items, 'workWorkersWork', '/content/social/realized-work/list/workers-work');
                }
                if (featureOn.TANGIBLE_DEPOSITS && tangibleDepositRead) {
                    this.prepMenuOption(workItems.items, 'workTangibleDeposits', '/content/social/realized-work/list/tangible-deposit');
                }
                if (workMovementRestrictionRead) {
                    this.prepMenuOption(workItems.items, 'workMovementRestriction', '/content/social/realized-work/list/movement-restriction');
                }
                if (featureOn.SUGGESTION && workSuggestionRead) {
                    const suggestionItems = this.prepMenuOption(workItems.items, 'workSuggestion', undefined, undefined, undefined, undefined, []);
                    this.prepMenuOption(suggestionItems.items, 'workSuggestionSatisfaction', '/content/social/realized-work/list/satisfaction');
                    this.prepMenuOption(suggestionItems.items, 'workSuggestionComplaint', '/content/social/realized-work/list/complaint');
                    this.prepMenuOption(suggestionItems.items, 'workSuggestionFinding', '/content/social/realized-work/list/finding');
                }
                if (operationalWorkRead) {
                    this.prepMenuOption(workItems.items, 'workOperationalReports', '/content/social/realized-work/list/operational-reports');
                    if (featureOn.NEW_CALENDAR) {
                        this.prepMenuOption(workItems.items, 'workOperationalWorks', '/content/social/realized-work/list/operational-work');
                    }
                }
                if (useContinuousRecords && continuousReportRead) {
                    this.prepMenuOption(workItems.items, 'workContinuousRecords', '/content/social/realized-work/list/continuous-record');
                }
                if (workSanctionRead) {
                    this.prepMenuOption(workItems.items, 'workSanction', '/content/social/realized-work/list/sanction');
                }
                if (featureOn.SERVICE_ANNOUNCEMENT && isAllowed('serviceAnnouncement.read')()) {
                    this.prepMenuOption(workItems.items, 'serviceAnnouncements', '/content/social/realized-work/list/service-announcements');
                }
            }

            if (featureOn.NEW_CALENDAR && isAllowed('calendar.read')()
                && this.globalGlobalStore.showMenuItem(SERVICE_SETTINGS_CALENDAR_TYPE_KEY)()) {
                this.prepMenuOption(sublinks, 'social-calendar', '/content/social/calendar', 'pi pi-pw pi-calendar-plus');
            }

            if (isAllowed('billing.read')() && featureOn.SOCIAL_PAYMENTS_AND_BILLING) {
                const billings: MenuItem[] = [];
                this.prepMenuOption(billings, 'billing-social-billing', '/content/social/billing/batch');
                this.prepMenuOption(billings, 'billing-social-billing-advance', '/content/social/billing/batch-advance');
                this.prepMenuOption(billings, 'billing-unbilled-work', '/content/social/billing/unbilled-work');
                this.prepSocialBillingDocumentsMenu(billings);

                this.prepMenuOption(sublinks, 'billing', undefined, 'pi pi-pw pi-money-bill', undefined, undefined, billings);
            }

            if (useBasicSocialConsulting && isAllowed('basicSocialConsulting.read')()) {
                const basicSocialConsulting = this.prepMenuOption(sublinks, 'basicSocialConsulting', '/content/social/basic-social-consulting');
                basicSocialConsulting.svgIcon = 'person-lines-fill';
            }

            if (isAllowed('waitingList.read')()) {
                this.prepMenuOption(sublinks, 'waitingList', '/content/social/waiting-list', 'pi pi-book');
            }

            if (useDiet && isAllowed('serviceDiet.read')()) {
                this.prepMenuOption(sublinks, 'diet', '/content/social/diet', 'fork-and-knife');
            }

            if (featureOn.USER_MESSAGES && isAllowed('serviceMessage.read')()) {
                this.prepMenuOption(sublinks, 'serviceMessages', '/content/social/service-messages', 'pi pi-comments');
            }

            const showFinancialDepositTools =
                readFolder &&
                featureOn.SOCIAL_FINANCIAL_DEPOSIT &&
                this.globalGlobalStore.showMenuItem(
                    SOCIAL_SERVICE_SETTINGS_FINANCIAL_DEPOSIT_TYPE_KEY,
                )();
            const allowedSocialStatistics = this.globalGlobalStore.socialServiceStore.allowStatistics();

            if (
                featureOn.SOCIAL_STATISTICS
                && isAllowed('statistics.read')()
                && Object.keys(allowedSocialStatistics).length > 0
            ) {
                const createSocialStatisticsMenuItem = (type: SocialStatisticsTypeEnum): MenuItem => ({
                    label: translate.instant(`statistics.social.types.${type}`),
                    routerLink: `/content/social/statistics/${type}`,
                    visible: true,
                    separator: false,
                });
                const isStatisticsTypeEnabled = (type: SocialStatisticsTypeEnum): boolean => {
                    const group = getSocialStatisticGroupByType(type);
                    return (allowedSocialStatistics[group]?.[type]?.length ?? 0) > 0;
                };
                const isStatisticsTypeVisible = (type: SocialStatisticsTypeEnum): boolean => {
                    if (!isStatisticsTypeEnabled(type)) return false;

                    switch (type) {
                        case SocialStatisticsTypeEnum.MATERIAL_ISSUES:
                            return featureOn.WAREHOUSE_MATERIAL && isAllowed('warehouseMaterial.read')();
                        case SocialStatisticsTypeEnum.BILLING:
                        case SocialStatisticsTypeEnum.BILLING_DETAIL:
                        case SocialStatisticsTypeEnum.BILLING_PERMANENT_RESIDENCE_MUNICIPALITY:
                        case SocialStatisticsTypeEnum.BILLING_PERMANENT_RESIDENCE_ORP:
                        case SocialStatisticsTypeEnum.BILLING_SERVICE_MUNICIPALITY:
                        case SocialStatisticsTypeEnum.BILLING_SERVICE_ORP:
                        case SocialStatisticsTypeEnum.BILLING_SERVICE_WORKPLACE:
                        case SocialStatisticsTypeEnum.BILLING_ITEMS:
                        case SocialStatisticsTypeEnum.BILLING_ITEMS_OFFERED_ACTIVITIES:
                        case SocialStatisticsTypeEnum.BILLING_ITEMS_OFFERED_ACTIVITY_GROUPS:
                            return featureOn.SOCIAL_PAYMENTS_AND_BILLING && isAllowed('billing.read')();
                        case SocialStatisticsTypeEnum.FINANCIAL_DEPOSIT_CLIENT:
                        case SocialStatisticsTypeEnum.FINANCIAL_DEPOSITS_CLIENTS_SUMMARY:
                            return showFinancialDepositTools;
                        case SocialStatisticsTypeEnum.ORDERED_MEALS_CLIENTS:
                        case SocialStatisticsTypeEnum.ORDERED_MEALS_OFFER:
                            return useDiet && isAllowed('serviceDiet.read')();
                        case SocialStatisticsTypeEnum.ACHO:
                            return achoAnalysisRead;
                        default:
                            return true;
                    }
                };

                const statisticsItems = Object.values(SocialStatisticsTypeGroupEnum)
                    .map((group): MenuItem | null => {
                        const groupTypes = getSocialStatisticsGroupTypes(group);
                        const visibleTypes = groupTypes.filter(isStatisticsTypeVisible);
                        if (visibleTypes.length === 0) return null;
                        if (groupTypes.length === 1) {
                            return createSocialStatisticsMenuItem(visibleTypes[0]);
                        }

                        return {
                            label: translate.instant(`statistics.social.groupTypes.${group}`),
                            visible: true,
                            separator: false,
                            items: visibleTypes.map(createSocialStatisticsMenuItem),
                        };
                    })
                    .filter((item): item is MenuItem => item !== null);

                if (statisticsItems.length > 0) {
                    this.prepMenuOption(
                        sublinks,
                        'statistics',
                        undefined,
                        'pi pi-chart-bar',
                        undefined,
                        undefined,
                        statisticsItems,
                    );
                }
            }

            const showProvidedOperationsReports = providedOperationsReportRead
                && featureOn.PROVIDED_OPERATIONS_REPORTS
                && this.useCard(
                    servicesWithAllowedProvidedOperationsReports(this.globalGlobalStore.organizationStore.services()),
                    ServiceTypeEnum.SOCIAL,
                    SERVICE_SETTINGS_PROVIDED_OPERATIONS_REPORTS_KEY,
                );

            const showFinancialIncomeTools =
                readFolder &&
                featureOn.SOCIAL_FINANCIAL_DEPOSIT &&
                services.some((service) => {
                    const settings = getServiceSettings(
                        service.serviceSettings,
                        SOCIAL_SERVICE_CONTRACTS_KEY,
                        SOCIAL_SERVICE_CONTRACTS_DEFAULT,
                    ) as SocialServiceSettingsContractsType;

                    return (
                        service.type === ServiceTypeEnum.SOCIAL &&
                        settings.showIncomes === true
                    );
                });

            const showCareContributionRefundTool =
                careContributionRefundRead &&
                readFolder &&
                featureOn.SOCIAL_FINANCIAL_DEPOSIT &&
                services.some((service) =>
                    service.type === ServiceTypeEnum.SOCIAL
                    && serviceTypeNameAllowed.careContributionRefund.includes(service.serviceType.name)
                    && this.globalGlobalStore.isAllowedForService('folder.read', service.id)());

            if (
                showProvidedOperationsReports ||
                showFinancialIncomeTools ||
                showFinancialDepositTools ||
                showCareContributionRefundTool
            ) {
                const toolItems: MenuItem[] = [];
                if (showProvidedOperationsReports) {
                    this.prepMenuOption(toolItems, 'providedOperationsReports', '/content/social/tools/provided-operations-reports');
                }
                if (showFinancialIncomeTools) {
                    if (pensionsIncomeRead) {
                        this.prepMenuOption(toolItems, 'pensionsIncomeTool', '/content/social/tools/pensions-income-tool');
                    }
                    if (contractualSurchargeIncomeRead) {
                        this.prepMenuOption(toolItems, 'contractualSurchargeIncomeTool', '/content/social/tools/contractual-surcharge-income-tool');
                    }
                    this.prepMenuOption(toolItems, 'socialContributionIncomeTool', '/content/social/tools/social-contribution-income-tool');
                    if (deficitStatusRead) {
                        this.prepMenuOption(toolItems, 'deficitTool', '/content/social/tools/deficit-tool');
                    }
                }
                if (showFinancialDepositTools) {
                    if (financialDepositBulkCreate) {
                        this.prepMenuOption(toolItems, 'financialDepositBulkTool', '/content/social/tools/financial-deposit-bulk-tool');
                    }
                    if (financialMovementsRead) {
                        this.prepMenuOption(toolItems, 'financialMovementsTool', '/content/social/tools/financial-movements-tool');
                    }
                }
                if (showCareContributionRefundTool) {
                    this.prepMenuOption(
                        toolItems,
                        'careContributionRefundTool',
                        '/content/social/tools/care-contribution-refund-tool',
                    );
                }

                if (toolItems.length) {
                    const tools = this.prepMenuOption(sublinks, 'tools', undefined, undefined, undefined, undefined, toolItems);
                    tools.svgIcon = 'wrench';
                }
            }

            if (sublinks.length > 0) {
                this.prepMenuOption(options, 'social-services', undefined, 'pi pi-pw pi-users', undefined, undefined, sublinks);
            }
        }

        // Health care services
        if (hasHealthCareService) {
            const healthCareMenu: MenuItem[] = [];
            if (readFolder) {
                this.prepMenuOption(healthCareMenu, 'client-folders', '/content/health-care/folders', 'pi pi-fw pi-folder');
            }
            if (readFile) {
                this.prepMenuOption(healthCareMenu, 'files-manager', '/content/health-care/files', 'pi pi-fw pi-server');
            }

            if (createWork || isAllowed('doctorWork.create')() || isAllowed('specialistWork.create')() || isAllowed('operationalWork.create')()) {
                this.prepMenuOption(healthCareMenu, 'works', '/content/health-care/works', 'pi pi-fw pi-plus', undefined, true);
            }
            if (readWork || readDoctorWork || readSpecialistWork || operationalWorkRead) {
                const workItems = this.healthCareWorkItems();

                if (workItems.length) {
                    this.prepMenuOption(healthCareMenu, 'realizedWork', undefined, 'pi pi-pw pi-briefcase', undefined, undefined, workItems);
                }

                if (isAllowed(healthcareCalendarOperation(ActionEnum.READ))() && this.globalGlobalStore.showMenuItem(healthcareCalendarSettingsKey())()) {
                    this.prepMenuOption(healthCareMenu, 'scheduler', '/content/health-care/scheduler', 'pi pi-pw pi-calendar-plus');
                }
            }
            if (isAllowed('billing.read')()) {
                const billings: MenuItem[] = [];
                this.prepMenuOption(billings, 'billingCreateNew', '/content/health-care/billing/create');
                this.prepMenuOption(billings, 'billingRefused', '/content/health-care/billing/refused');
                this.prepMenuOption(billings, 'billingUnbilledWork', '/content/health-care/billing/unbilled');
                this.prepMenuOption(billings, 'billingAccountingOutput', '/content/health-care/billing/output');

                const insurancesWithBillings = this.data.getInsuranceCompanies();
                if (insurancesWithBillings?.['items'].length > 0) {
                    const insurance = this.prepMenuOption(billings, 'billingCreatedBillings', undefined, undefined, undefined, undefined, []);
                    insurance.items = insurancesWithBillings?.['items']?.map((it: any) => {
                        return {
                            label: it.code,
                            routerLink: `/content/health-care/billing/created/${it.code}`,
                            routerLinkActiveOptions: <IsActiveMatchOptions>{
                                paths: 'subset',
                                queryParams: 'ignored',
                                matrixParams: 'ignored',
                                fragment: 'ignored',
                            },
                            visible: !isOffline(),
                            separator: false,
                        };
                    });
                }

                this.prepMenuOption(healthCareMenu, 'billing', undefined, 'pi pi-pw pi-money-bill', undefined, undefined, billings);
            }

            const allowedStatistics = this.globalGlobalStore.medicalServiceStore.allowStatistics();
            if (isAllowed('statistics.read')() && Object.keys(allowedStatistics).length > 0) {
                const stats = this.prepMenuOption(healthCareMenu, 'statistics', undefined, 'pi pi-chart-bar', undefined, undefined, []);
                stats.items = Object.keys(allowedStatistics).map((group) => {
                    const hasItems = getStatisticsGroupTypes(group);
                    const subKeys = Object.keys(allowedStatistics[group]);
                    const featureVisible = !isOffline() && group === StatisticsTypeGroupEnum.PURO ? featureOn.PURO : true;
                    const visible = featureVisible
                        && (group !== StatisticsTypeGroupEnum.ACHO || achoAnalysisRead);
                    return {
                        label: translate.instant(`statistics.groupTypes.${group}`),
                        routerLink: hasItems.length <= 1 ? `/content/health-care/statistics/${group}` : undefined,
                        visible,
                        separator: false,
                        items: hasItems.length > 1
                            ? subKeys.map((type) => {
                                return {
                                    label: translate.instant(`statistics.types.${type}`),
                                    routerLink: `/content/health-care/statistics/${type}`,
                                    visible,
                                    separator: false,
                                };
                            })
                            : undefined,
                    };
                });
            }

            if (readWork || readDoctorWork || createWork) {
                const toolItems: MenuItem[] = [];
                if (isAllowed('administration.read')() && expertiseCodes.find((code) => ['902', '913', '925'].includes(code))) {
                    this.prepMenuOption(toolItems, 'toolsBatchPrint', '/content/health-care/tools/batch-print');
                }
                if (createWork && expertiseCodes.find((code) => ['926'].includes(code))) {
                    this.prepMenuOption(toolItems, 'toolsMissingProcedures', '/content/health-care/tools/missing-procedures');
                }
                if (readDoctorWork && expertiseCodes.find((code) => ['926'].includes(code))) {
                    this.prepMenuOption(toolItems, 'toolsMissingDoctorRecords', '/content/health-care/tools/missing-doctor-records');
                }
                if (expertiseCodes.find((code) => ['913'].includes(code))) {
                    this.prepMenuOption(toolItems, 'toolsMedicationPreparation', '/content/health-care/tools/medication-preparation');
                }

                if (toolItems.length) {
                    const tools = this.prepMenuOption(healthCareMenu, 'tools', undefined, undefined, undefined, undefined, toolItems);
                    tools.svgIcon = 'wrench';
                }
            }

            if (healthCareMenu.length > 0) {
                this.prepMenuOption(options, 'health-services', undefined, 'pi pi-pw pi-heart', undefined, true, healthCareMenu);
            }
        }

        // MADO
        if (isAllowed('madoAccount.read')() || isAllowed('madoAccount.create')()) {
            const mado = this.prepMenuOption(options, 'accounting', undefined, 'pi pi-fw pi-folder', undefined, undefined, []);
            this.prepMenuOption(mado.items, 'madoDashboard', '/accounting/dashboard');
            this.prepMenuOption(mado.items, 'madoJournalRecords', '/accounting/journal-records');
            this.prepMenuOption(mado.items, 'madoJournalEntries', '/accounting/journal-entries');
            this.prepMenuOption(mado.items, 'madoAccounts', '/accounting/accounts');
            this.prepMenuOption(mado.items, 'madoCurrencyRates', '/accounting/currency-rates');
            this.prepMenuOption(mado.items, 'madoInvoicesIssued', '/accounting/invoices-issued');
            this.prepMenuOption(mado.items, 'madoInvoicesReceived', '/accounting/invoices-received');
            this.prepMenuOption(mado.items, 'madoCashRegister', '/accounting/cash-register');
            this.prepMenuOption(mado.items, 'madoBank', '/accounting/bank');
            this.prepMenuOption(mado.items, 'madoInternalDocuments', '/accounting/internal-documents');

            if (isAllowed('madoReport.read')() || isAllowed('madoReport.create')()) {
                this.prepMenuOption(mado.items, 'madoReports', '/accounting/reports');
                this.prepMenuOption(mado.items, 'madoReportGeneralLedger', '/accounting/reports/general-ledger');

                if (isAllowed('madoCreditAgreements.read')()) {
                    const credit = this.prepMenuOption(options, 'madoLoanAgenda', undefined, 'pi pi-fw pi-money-bill', undefined, undefined, []);
                    this.prepMenuOption(credit.items, 'creditAgreements', '/accounting/loan-agreements');
                    this.prepMenuOption(credit.items, 'madoCreditTransactions', '/accounting/loan-transactions');
                    this.prepMenuOption(credit.items, 'madoLoanPayments', '/accounting/loan-payments');
                    this.prepMenuOption(credit.items, 'madoLoanReports', '/accounting/loan-reports');
                }
            }
        }

        const createService = isAllowed('service.create')();
        const updateService = isAllowed('service.update')();
        const createUser = isAllowed('user.create')();
        const updateUser = isAllowed('user.update')();
        const createOrganization = isAllowed('organization.create')();
        const updateOrganization = isAllowed('organization.update')();

        if (updateOrganization || updateUser || updateService || createOrganization || createService) {
            const settingLinks: MenuItem[] = [];
            if (isAllowed('administration.read')()) {
                if (updateOrganization || createOrganization) {
                    this.prepMenuOption(settingLinks, 'organizations', '/content/organizations');
                }
                if (allowedHealthCareAndSocial && (createService || updateService)) {
                    this.prepMenuOption(settingLinks, 'services', '/content/services');
                }
                if (updateUser || createUser) {
                    this.prepMenuOption(settingLinks, 'users', '/content/users');
                }
            }

            if (isAllowed('addressBook.read')()) {
                // This empty menu-list adds header to the menu in side view
                if (!['slim', 'compact', 'horizontal'].includes(menu?.menuType || '') && !hasSubMenu()) {
                    this.prepMenuOption(options, 'addressBook', undefined, 'pi pi-pw pi-card', undefined, undefined, []);
                }
                this.prepMenuOption(options, 'addressBook', '/address-book', 'pi pi-pw pi-id-card', undefined);
            }

            if (isAllowed('system.read', 'systemPuro.read', 'systemEnum.read', 'diagnosisBonification.read')()) {
                this.prepMenuOption(settingLinks, 'system', '/content/system');
            }

            if (settingLinks.length) {
                this.prepMenuOption(options, 'settings', undefined, 'pi pi-pw pi-cog', undefined, undefined, settingLinks);
            }

        }

        if (featureOn.STORYBOOK) {
            // This empty menu-list adds header to the menu in side view
            if (!['slim', 'compact', 'horizontal'].includes(menu?.menuType || '') && !hasSubMenu()) {
                this.prepMenuOption(options, 'dev', undefined, 'pi pi-pw pi-code', undefined, undefined, []);
            }
            this.prepMenuOption(options, 'storybook', '/examples', 'pi pi-pw pi-code', undefined);
        }

        return options;
    });

    public expertiseCodes = computed(() => {
        const services = this.services();
        return services?.map(s => s.expertise?.code || '') || [];
    });

    public healthCareWorkItems = computed(() => {
        const expertiseCodes = this.expertiseCodes();
        const readWork = this.globalGlobalStore.isAllowed('work.read')();
        const readDoctorWork = this.globalGlobalStore.isAllowed('doctorWork.read')();
        const readSpecialistWork = this.globalGlobalStore.isAllowed('specialistWork.read')();
        const operationalWorkRead = this.globalGlobalStore.isAllowed('operationalWork.read')();

        const workItems: MenuItem[] = [];

        if (readWork) {
            if (expertiseCodes.find((code) => ['902', '925', '926'].includes(code))) {
                this.prepMenuOption(workItems, 'workVisits', '/content/health-care/realized-work/list/visit');
                this.prepMenuOption(workItems, 'workUnrealizedVisits', '/content/health-care/realized-work/list/unrealized-visit');
            }
            if (expertiseCodes.includes('926')) {
                this.prepMenuOption(workItems, 'extramuralCare', '/content/health-care/realized-work/list/extramural-care');
                this.prepMenuOption(workItems, 'workResiduaryCares', '/content/health-care/realized-work/list/residuary-care');
            }
            if (expertiseCodes.includes('913')) {
                this.prepMenuOption(workItems, 'workInterventions', '/content/health-care/realized-work/list/intervention');
            }
            this.prepMenuOption(workItems, 'performedProcedures', '/content/health-care/realized-work/list/performed-procedures');
            this.prepMenuOption(workItems, 'workDailyRecords', '/content/health-care/realized-work/list/daily-record');
        }

        if ((readDoctorWork || readWork) && expertiseCodes.find((code) => ['926', '720'].includes(code))) {
            this.prepMenuOption(workItems, 'workDoctorRecords', '/content/health-care/realized-work/list/doctor-record');
        }

        if ((readSpecialistWork || readWork) && expertiseCodes.includes('926')) {
            this.prepMenuOption(workItems, 'workSpecialistRecords', '/content/health-care/realized-work/list/specialist-record');
        }

        if (operationalWorkRead) {
            this.prepMenuOption(workItems, 'workOperationalReports', '/content/health-care/realized-work/list/operational-reports');
        }

        return workItems;
    });

    constructor() {
        super();
        // TODO; docasny hack pro presmerovani na prvni polozku v menu - po rozliseni organizaci smazat
        effect(() => {
            const sidebarOptions = this.sidebarOptions();
            const userRulesLoaded = this.globalGlobalStore.userRulesLoaded()();
            const settingsLoaded = this.globalGlobalStore.organizationStore.settingsLoaded()();
            const link = sidebarOptions[0]?.routerLink || sidebarOptions[0]?.items?.[0]?.routerLink;
            if (userRulesLoaded && settingsLoaded && sidebarOptions.length && link && router.url === '/') {
                router.navigateByUrl(link);
            }
        });

        this.api.getInsuranceCompanies({
            filter: [[{ column: 'existsBilling', operator: 'EQ', value: ['true'] }]],
            paging: { page: 1, itemsPerPage: 99 },
        });

        effect(() => {
            const servicesLoaded = this.globalGlobalStore.organizationStore.servicesInContext()?.length;
            if (!servicesLoaded) return;

            void this.api.getSocialBillingDocumentCounts();
        });

        this.onSuccess(
            () => {
                void this.api.getSocialBillingDocumentCounts();
            },
            'createSocialPartialBilling',
            'createSocialNewBilling',
            'deleteSocialBillingDocument',
        );
    }

    private prepMenuOption(options: MenuItem[], key: string, routerLink?: string, icon?: string, badge?: string, visible?: boolean, items?: MenuItem[]): AppMenuItem {
        const opt = {
            label: translate.instant(`sidebar.${key}`),
            icon,
            badge,
            routerLink,
            visible: visible ?? !isOffline(),
            separator: false,
            items: items ?? (routerLink === undefined ? [] : undefined),
        };
        options.push(opt);
        return opt as AppMenuItem;
    }

    private prepSocialBillingDocumentsMenu(options: MenuItem[]): void {
        const counts = this.data.getSocialBillingDocumentCounts() || [];
        const services = this.globalGlobalStore.organizationStore.servicesInContext();
        const serviceMap = new Map(services.map((service) => [Number(service.id), service]));
        const serviceCounts = new Map<number, {
            total: number;
            years: Map<number, { total: number; months: { month: number; count: number }[] }>;
        }>();

        counts.forEach((item) => {
            const serviceId = Number(item.serviceId || 0);
            const year = Number(item.year || 0);
            const month = Number(item.month || 0);
            const count = Number(item.count || 0);
            if (!serviceId || !year || !month || !count) return;

            const serviceData = serviceCounts.get(serviceId) || { total: 0, years: new Map() };
            const yearData = serviceData.years.get(year) || { total: 0, months: [] };

            yearData.total += count;
            yearData.months.push({ month, count });
            serviceData.total += count;
            serviceData.years.set(year, yearData);
            serviceCounts.set(serviceId, serviceData);
        });

        const documents = this.prepMenuOption(options, 'billing-documents', undefined, undefined, undefined, undefined, []);
        Array.from(serviceCounts.entries())
            .sort(([a], [b]) => (serviceMap.get(a)?.name || '').localeCompare(serviceMap.get(b)?.name || '', 'cs'))
            .forEach(([serviceId, serviceData]) => {
                const service = serviceMap.get(serviceId);
                const serviceItem: MenuItem = {
                    label: service?.name || serviceId.toString(),
                    badge: serviceData.total.toString(),
                    visible: true,
                    items: [],
                };

                Array.from(serviceData.years.entries())
                    .sort(([a], [b]) => b - a)
                    .forEach(([year, yearData]) => {
                        serviceItem.items!.push({
                            label: year.toString(),
                            badge: yearData.total.toString(),
                            visible: true,
                            items: yearData.months
                                .sort((a, b) => b.month - a.month)
                                .map((monthData) => ({
                                    label: this.formatMonth(monthData.month),
                                    badge: monthData.count.toString(),
                                    visible: true,
                                    routerLink: '/content/social/billing/documents',
                                    queryParams: {
                                        serviceId,
                                        year,
                                        month: monthData.month,
                                    },
                                })),
                        });
                    });

                documents.items.push(serviceItem);
            });
    }

    private formatMonth(month: number): string {
        const label = new Intl.DateTimeFormat('cs-CZ', { month: 'long' }).format(new Date(2000, month - 1, 1));

        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    private useCard(services: ServiceGridItemObject[], serviceType: string, settingKey: string, conditionFn?: CallableFunction): boolean {
        return services.some((service) => {
            if (service.type !== serviceType) return false;
            if (conditionFn && !conditionFn(service)) return false;
            const setting: any = service.serviceSettings.find((setting) => setting.key === settingKey) ?? { data: '{}' };
            let data = setting.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch {
                    return true;
                }
            }
            return data?.useCard ?? true;
        });
    }
}
